'use client';

import { useEffect, useState } from 'react';

/**
 * Pre-auth tenant branding fetched from /api/public/tenant-by-host (FF-101 / FF-102).
 *
 * Returns null when:
 *   - The fetch is still in flight (initial render)
 *   - The host is unmapped (server 404)
 *   - The fetch failed (network / server error)
 *
 * Callers should render a neutral typographic default when `branding === null`.
 */
export interface TenantBranding {
  slug: string;
  displayName: string;
  logoUrl: string | null;
  primaryColor: string | null;
  faviconUrl: string | null;
}

export interface UseTenantBrandingResult {
  loading: boolean;
  branding: TenantBranding | null;
}

export function useTenantBranding(): UseTenantBrandingResult {
  const [loading, setLoading] = useState(true);
  const [branding, setBranding] = useState<TenantBranding | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/public/tenant-by-host', { cache: 'no-store' })
      .then(async (r) => {
        if (!r.ok) return null;
        return (await r.json()) as TenantBranding;
      })
      .catch(() => null)
      .then((data) => {
        if (cancelled) return;
        setBranding(data);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { loading, branding };
}
